/**
 * ToolbarTooltip
 * Minimal tooltip wrapper for toolbar buttons.
 * Appears above the element via a portal to document.body.
 */

import { useState, useRef, useCallback, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface ToolbarTooltipProps {
  children: ReactNode;
  label: string;
}

export function ToolbarTooltip({ children, label }: ToolbarTooltipProps) {
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState({ x: 0, bottom: 0 });
  const wrapperRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = useCallback(() => {
    timerRef.current = setTimeout(() => {
      const el = wrapperRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setPos({
        x: rect.left + rect.width / 2,
        bottom: window.innerHeight - rect.top + 6,
      });
      setVisible(true);
    }, 400);
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setVisible(false);
  }, []);

  return (
    <div
      ref={wrapperRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{ display: 'inline-flex', alignItems: 'center' }}
    >
      {children}
      {visible &&
        createPortal(
          <div
            style={{
              position: 'fixed',
              left: pos.x,
              bottom: pos.bottom,
              transform: 'translateX(-50%)',
              backgroundColor: '#111827',
              color: '#fff',
              fontSize: 11,
              borderRadius: 4,
              padding: '3px 8px',
              pointerEvents: 'none',
              whiteSpace: 'nowrap',
              zIndex: 99999,
            }}
          >
            {label}
            {/* Arrow pointing down */}
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: '50%',
                transform: 'translateX(-50%)',
                width: 0,
                height: 0,
                borderLeft: '4px solid transparent',
                borderRight: '4px solid transparent',
                borderTop: '4px solid #111827',
              }}
            />
          </div>,
          document.body
        )}
    </div>
  );
}
